/* eslint-disable max-lines-per-function */
import { isNull, isUndefined, noop } from 'lodash';
import { ReactElement, useCallback, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { VisitSettlement } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { TaxIdentifier } from '@dreryk/tax-identifier-common-api';

import { TranslatableText, useBoolean } from '@dreryk/react-common';

import { protectFeature } from '~libs.shared.system';

import { VisitSettlementAdjustmentModal } from '../adjustmentModal/VisitSettlementAdjustmentModal';
import { VisitSettlementInvoiceCreateModalOpener } from '../extensions/visitForm/payment/VisitSettlementInvoiceCreateModal.opener';
import { VisitSettlementPaymentModal } from '../paymentModal/VisitSettlementPaymentModal';
import { VisitSettlementPaymentModalLoader } from '../paymentModal/VisitSettlementPaymentModal.loader';
import { visitSettlementTranslationKeys } from '../translations';

import { createAdjustmentFormBean } from './form/adjustment/adjustment.utils';
import { createPaymentFormBean, mapPaymentMethodApi } from './form/payment/payment.utils';
import { VisitDetailsFormBean } from './form/visitDetails/visitDetails.types';
import { createVisitDetailsFormBean } from './form/visitDetails/visitDetails.utils';
import { VisitSettlementFormInitializer } from './form/VisitSettlementForm';
import { VisitMedicalServiceTableBean } from './medicalServices/medicalServices.types';
import { mapToVisitMedicalServicesTableBean } from './medicalServices/medicalServices.utils';
import { performVisitSettlementCqrsFind } from './performVisitSettlementCqrsFind';

type UnsettledState = boolean | null;

const getInitialUnsettledState = (initialVisitSettlement: VisitSettlement | null | undefined) => (): UnsettledState => {
   if (isUndefined(initialVisitSettlement)) {
      return null;
   }

   if (isNull(initialVisitSettlement)) {
      return true;
   }

   return initialVisitSettlement.settlementStatus === 'UNSETTLED';
};

const getPaymentAmount = (visitSettlement: VisitSettlement): number | undefined => {
   if (visitSettlement?.paymentAmount) {
      return visitSettlement?.paymentAmount?.asNumber();
   }

   return visitSettlement.amountDue.asNumber() > 0 ? visitSettlement.amountDue.asNumber() : 0;
};

type UseVisitSettlementActionOptions = {
   initialVisitSettlement?: VisitSettlement | null;
   invoiceProviderIntegrationEnabled?: boolean;
   ownedByCurrentUser: boolean;
   visitDetails?: Omit<VisitDetailsFormBean, 'practitioner'>;
   appointmentId: number;
   practitionerId: number;
   paymentLabel: TranslatableText;
   adjustmentLabel: TranslatableText;
};

type UseVisitSettlementActionReturnFn = {
   visitUnsettled: UnsettledState;
   setVisitUnsettled: (state: UnsettledState) => void;
   setVisitSettlement: (visitSettlement: VisitSettlement | null) => void;
   visitSettlementPaymentModal: ReactElement | null;
   visitSettlementAdjustmentModal: ReactElement | null;
   label: TranslatableText;
   disabled: boolean;
   id: string;
   openModal: VoidFunction;
};

export const useVisitSettlementAction = (
   options: UseVisitSettlementActionOptions
): UseVisitSettlementActionReturnFn => {
   const {
      initialVisitSettlement,
      ownedByCurrentUser,
      visitDetails,
      practitionerId,
      appointmentId,
      adjustmentLabel,
      paymentLabel,
      invoiceProviderIntegrationEnabled,
   } = options;
   const canAdjustingSettlementOfYourOwnVisits =
      protectFeature('adjustingSettlementOfYourOwnVisits') && ownedByCurrentUser;
   const adjustingSettlementOfAllVisitsAvailable = protectFeature('adjustingSettlementOfAllVisits');
   const settlingMedicalServicesPerformedByOneselfAvailable = protectFeature(
      'settlingMedicalServicesPerformedByOneself'
   );

   const settlingMedicalServicesPerformedByAnyPractitionerAvailable = protectFeature(
      'settlingMedicalServicesPerformedByAnyPractitioner'
   );

   const [visitUnsettled, setVisitUnsettled] = useState<UnsettledState>(
      getInitialUnsettledState(initialVisitSettlement)
   );

   const [visitSettlement, setVisitSettlement] = useState<VisitSettlement | null>(initialVisitSettlement ?? null);
   const [taxIdentifier, setTaxIdentifier] = useState<TaxIdentifier>();

   const [
      { setTrue: openVisitSettlementPaymentModal, setFalse: closeVisitSettlementPaymentModal },
      showVisitSettlementPaymentModal,
   ] = useBoolean(false);
   const [
      { setTrue: openVisitSettlementAdjustmentModal, setFalse: closeVisitSettlementAdjustmentModal },
      showVisitSettlementAdjustmentModal,
   ] = useBoolean(false);

   const markVisitAsSettled = useCallback(() => {
      setVisitUnsettled(false);
   }, [setVisitUnsettled]);

   const formInitializer: VisitSettlementFormInitializer = () => {
      const medicalServices: VisitMedicalServiceTableBean[] =
         visitSettlement?.medicalServices.map(mapToVisitMedicalServicesTableBean) ?? [];
      const priceChangeReason = visitSettlement?.priceChangeReason ?? null;

      const adjustment = createAdjustmentFormBean({
         adjustment: visitSettlement?.adjustment,
         canAdjustment:
            (adjustingSettlementOfAllVisitsAvailable || canAdjustingSettlementOfYourOwnVisits) && !visitUnsettled,
      });

      const previousPayment =
         adjustment && visitSettlement
            ? {
                 amount: getPaymentAmount(visitSettlement),
                 method: visitSettlement.paymentMethod
                    ? mapPaymentMethodApi(visitSettlement.paymentMethod.methodType)
                    : undefined,
                 methodDescription: visitSettlement.paymentMethod?.description,
              }
            : undefined;

      const payment = createPaymentFormBean({
         medicalServices: medicalServices,
         required: Boolean(adjustment) || !ownedByCurrentUser,
         previousPaymentAmount: previousPayment?.amount,
         previousPaymentMethod: previousPayment?.method,
         previousPaymentMethodDescription: previousPayment?.methodDescription,
         advancePayments: visitSettlement?.advancePayments,
      });

      const settlementAdjustmentOfAllVisitsAllowed = adjustment && adjustingSettlementOfAllVisitsAvailable;
      const settlementAdjustmentOfOwnVisitsAllowed =
         adjustment && ownedByCurrentUser && canAdjustingSettlementOfYourOwnVisits;
      const settlementOfAllVisitsAllowed = !adjustment && settlingMedicalServicesPerformedByAnyPractitionerAvailable;
      const settlementOfOwnVisitsAllowed =
         !adjustment && ownedByCurrentUser && settlingMedicalServicesPerformedByOneselfAvailable;

      return {
         visitDetails: visitDetails ? createVisitDetailsFormBean(visitDetails, visitSettlement) : undefined,
         visitSettlement: {
            advancePayments: visitSettlement?.advancePayments,
            medicalServices: medicalServices,
            priceChangeReason: priceChangeReason,
            editable:
               settlementAdjustmentOfAllVisitsAllowed ||
               settlementAdjustmentOfOwnVisitsAllowed ||
               settlementOfAllVisitsAllowed ||
               settlementOfOwnVisitsAllowed,
            ownedByCurrentUser: ownedByCurrentUser,
         },
         adjustment: adjustment,
         payment: payment,
      };
   };

   const adjustmentAvailable =
      visitUnsettled === false && (adjustingSettlementOfAllVisitsAvailable || canAdjustingSettlementOfYourOwnVisits);

   const paymentAvailable =
      visitUnsettled === true &&
      (settlingMedicalServicesPerformedByAnyPractitionerAvailable ||
         (ownedByCurrentUser && settlingMedicalServicesPerformedByOneselfAvailable));

   const openModal = useCallback(() => {
      if (adjustmentAvailable) {
         return openVisitSettlementAdjustmentModal();
      }

      if (paymentAvailable) {
         return openVisitSettlementPaymentModal();
      }

      return noop();
   }, [adjustmentAvailable, openVisitSettlementAdjustmentModal, openVisitSettlementPaymentModal, paymentAvailable]);

   useUpdateEffect(() => {
      setVisitUnsettled(getInitialUnsettledState(initialVisitSettlement));
      setVisitSettlement(initialVisitSettlement ?? null);
   }, [initialVisitSettlement]);

   return {
      visitUnsettled: Boolean(visitUnsettled),
      setVisitUnsettled: setVisitUnsettled,
      setVisitSettlement: setVisitSettlement,
      visitSettlementPaymentModal: (
         <VisitSettlementInvoiceCreateModalOpener
            patientId={visitDetails!.patient.id!}
            visitDate={visitDetails?.beginDate}
            appointmentId={appointmentId}
            visitSettlementId={visitSettlement?.id}
            taxIdentifier={taxIdentifier}
         >
            {({ openModal: openInvoiceCreateModal }) =>
               showVisitSettlementPaymentModal ? (
                  <VisitSettlementPaymentModalLoader>
                     {({ fiscalPrintEnabled }) => (
                        <VisitSettlementPaymentModal
                           title={
                              ownedByCurrentUser
                                 ? visitSettlementTranslationKeys.paymentModal.title.settlement
                                 : visitSettlementTranslationKeys.paymentModal.title.settlementSummary
                           }
                           appointmentId={appointmentId}
                           visitDate={visitDetails!.beginDate}
                           practitionerId={practitionerId}
                           initializer={formInitializer}
                           onCancel={closeVisitSettlementPaymentModal}
                           onPayment={markVisitAsSettled}
                           onSuccess={async ({ withInvoiceCreation, visitSettlement, taxIdentifierForReceipt }) => {
                              closeVisitSettlementPaymentModal();

                              if (withInvoiceCreation) {
                                 const settlement =
                                    visitSettlement || (await performVisitSettlementCqrsFind(appointmentId));
                                 setVisitSettlement(settlement);
                                 setTaxIdentifier(taxIdentifierForReceipt);
                                 openInvoiceCreateModal();
                              }
                           }}
                           ownedByCurrentUser={ownedByCurrentUser}
                           fiscalPrintEnabled={fiscalPrintEnabled}
                           invoiceProviderIntegrationEnabled={invoiceProviderIntegrationEnabled}
                        />
                     )}
                  </VisitSettlementPaymentModalLoader>
               ) : null
            }
         </VisitSettlementInvoiceCreateModalOpener>
      ),
      visitSettlementAdjustmentModal: showVisitSettlementAdjustmentModal ? (
         <VisitSettlementAdjustmentModal
            appointmentId={appointmentId}
            practitionerId={practitionerId}
            initializer={formInitializer}
            onCancel={closeVisitSettlementAdjustmentModal}
            onSuccess={closeVisitSettlementAdjustmentModal}
         />
      ) : null,
      id: adjustmentAvailable ? 'visitSettlementAdjustment' : 'visitSettlementPayment',
      label: adjustmentAvailable ? adjustmentLabel : paymentLabel,
      disabled: !adjustmentAvailable && !paymentAvailable,
      openModal: openModal,
   };
};
